import { Moon, Sun } from 'lucide-react';
import { useStore } from '@/state/store';
import { AVAILABLE_VOICING_FAMILY_SELECTION_IDS } from '@/state/voicingAvailability';
import { getChordPlayer } from '@/audio/player';
import { useWakeLockStatus, wakeLockSupported } from '@/components/wakeLock';
import { useKeepAwakeFallbackActive } from '@/components/keepAwakeFallback';
import {
  voicingFamilySelectionDescription,
  voicingFamilySelectionLabel,
  type VoicingFamilySelection,
} from '@/music/voicingFamilies';

// The set-and-forget options live in the navigation DRAWER (YouTube-shell
// rework, Aug 2026) — one surface for destinations and preferences, so the
// header stays a single uncluttered row at every width. The old cog popover
// and the standalone theme button are gone; the theme is an Appearance row.
export function PreferencesPanel() {
  const defaultVoicingFamily = useStore((s) => s.defaultVoicingFamily);
  const setDefaultVoicingFamily = useStore((s) => s.setDefaultVoicingFamily);
  const stringSet = useStore((s) => s.stringSet);
  const setStringSet = useStore((s) => s.setStringSet);
  const freeStringSet = useStore((s) => s.freeStringSet);
  const setFreeStringSet = useStore((s) => s.setFreeStringSet);
  const avoidB9 = useStore((s) => s.avoidB9);
  const setAvoidB9 = useStore((s) => s.setAvoidB9);
  const audioEnabled = useStore((s) => s.audioEnabled);
  const setAudioEnabled = useStore((s) => s.setAudioEnabled);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const keepAwake = useStore((s) => s.keepAwake);
  const setKeepAwake = useStore((s) => s.setKeepAwake);
  // Stable for the life of the page — no state needed.
  const canKeepAwake = wakeLockSupported();
  const wakeStatus = useWakeLockStatus();
  const fallbackActive = useKeepAwakeFallbackActive();

  return (
    <div className="settings-panel">
      <section className="settings-section" aria-labelledby="appearance-settings-heading">
        <h3 id="appearance-settings-heading" className="settings-section-title">
          Appearance
        </h3>
        <div className="settings-row">
          <span className="settings-label">Theme</span>
          <div className="segmented" role="group" aria-label="Colour theme">
            <button
              type="button"
              className={theme === 'light' ? 'active' : ''}
              onClick={() => theme !== 'light' && toggleTheme()}
              aria-pressed={theme === 'light'}
              data-testid="theme-light"
            >
              <Sun size={14} aria-hidden="true" /> Light
            </button>
            <button
              type="button"
              className={theme === 'dark' ? 'active' : ''}
              onClick={() => theme !== 'dark' && toggleTheme()}
              aria-pressed={theme === 'dark'}
              data-testid="theme-dark"
            >
              <Moon size={14} aria-hidden="true" /> Dark
            </button>
          </div>
        </div>
        {/* Chord notation (∆ / − / spelled) moved to the workspace's Chords
            menu, beside the guitar/piano diagram switch — how chords READ is
            one decision, made where the chords are. */}
      </section>

      {/* These are the app-wide DEFAULTS for the pages that have no lens —
          Progressions and the chord reference read them with no controls of
          their own. On the workbench the same fields are offered where they
          act: the lens row's Options panel (SequenceBuilderPage). Both bind
          the one store field through the one setter — the compPianoFamily
          precedent — so the two places can never disagree. */}
      <section className="settings-section" aria-labelledby="voicing-settings-heading">
        <h3 id="voicing-settings-heading" className="settings-section-title">
          Voicing defaults
        </h3>
        <div className="settings-row">
          <label className="settings-label" htmlFor="default-voicing-family">
            Default voicing family
          </label>
          <select
            id="default-voicing-family"
            value={defaultVoicingFamily}
            onChange={(event) =>
              setDefaultVoicingFamily(event.target.value as VoicingFamilySelection)
            }
            aria-label="Default voicing family"
            data-testid="default-voicing-family"
          >
            {AVAILABLE_VOICING_FAMILY_SELECTION_IDS.map((selection) => (
              <option
                key={selection}
                value={selection}
                title={voicingFamilySelectionDescription(selection)}
              >
                {voicingFamilySelectionLabel(selection)}
              </option>
            ))}
          </select>
        </div>
        {defaultVoicingFamily !== 'joe-pass' && (
          <>
            <div className="settings-row">
              <span className="settings-label">Home register</span>
              <div className="segmented" role="group" aria-label="Home register">
                <button
                  type="button"
                  className={stringSet === 'middle' ? 'active' : ''}
                  onClick={() => setStringSet('middle')}
                  aria-pressed={stringSet === 'middle'}
                  data-testid="stringset-middle"
                >
                  Lower
                </button>
                <button
                  type="button"
                  className={stringSet === 'upper' ? 'active' : ''}
                  onClick={() => setStringSet('upper')}
                  aria-pressed={stringSet === 'upper'}
                  data-testid="stringset-upper"
                >
                  Upper
                </button>
              </div>
            </div>
            <label
              className="switch"
              title="Voice-lead across both layouts for this family — the home layout starts the line, and later chords may change layout when it means less movement"
            >
              <input
                type="checkbox"
                checked={freeStringSet}
                onChange={(event) => setFreeStringSet(event.target.checked)}
                data-testid="free-stringset"
              />
              Allow layout changes
            </label>
          </>
        )}
        <label className="switch" title="Flag voicings with a minor-9th (b9) clash">
          <input
            type="checkbox"
            checked={avoidB9}
            onChange={(event) => setAvoidB9(event.target.checked)}
            data-testid="avoid-b9"
          />
          Avoid b9
        </label>
      </section>

      <section className="settings-section" aria-labelledby="audio-settings-heading">
        <h3 id="audio-settings-heading" className="settings-section-title">
          Audio
        </h3>
        <label className="switch" title="Play chords on click">
          <input
            type="checkbox"
            checked={audioEnabled}
            onChange={(event) => {
              setAudioEnabled(event.target.checked);
              if (event.target.checked) void getChordPlayer().resume();
              else getChordPlayer().stop();
            }}
            data-testid="audio-toggle"
          />
          Sound
        </label>
        <div className="settings-row">
          <span className="settings-label">Audio engine</span>
          <button
            type="button"
            className="btn-ghost btn btn-sm"
            onClick={() => void getChordPlayer().resetAudio()}
            title="Rebuild the audio connection after an output-device or CarPlay problem"
            data-testid="audio-reset"
          >
            Reset
          </button>
        </div>
      </section>

      <section className="settings-section" aria-labelledby="device-settings-heading">
        <h3 id="device-settings-heading" className="settings-section-title">
          Device
        </h3>
        {/* Rendered disabled rather than hidden where the API is missing — a
            row that only exists on the right browser reads as the option not
            existing at all (the Pages-row lesson). */}
        <label
          className="switch"
          title="Hold the screen on while the app is open — a chart on a music stand must not sleep mid-tune. Turn off to let the phone sleep as usual."
        >
          <input
            type="checkbox"
            checked={keepAwake}
            disabled={!canKeepAwake}
            onChange={(event) => setKeepAwake(event.target.checked)}
            data-testid="keep-awake"
          />
          Keep screen awake
        </label>
        {!canKeepAwake && (
          <p className="settings-note">
            Not supported by this browser — the screen will sleep as usual.
          </p>
        )}
        {/* A best-effort lock that fails silently is indistinguishable from a
            broken one from a music stand — name what is actually in force. */}
        {canKeepAwake && keepAwake && wakeStatus === 'held' && (
          <p className="settings-note" data-testid="keep-awake-status">
            Holding — the screen stays on while the app is open.
          </p>
        )}
        {canKeepAwake && keepAwake && wakeStatus === 'declined' && (
          <p className="settings-note" data-testid="keep-awake-status">
            {fallbackActive
              ? 'Holding — via media playback (Low Power Mode declined the wake lock).'
              : 'The browser declined (Low Power Mode?) — tap anywhere to hold the screen another way.'}
          </p>
        )}
      </section>
    </div>
  );
}
